import React, { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Upload, X, Loader2 } from 'lucide-react';
import { sanitizeFileName } from '../../lib/utils';

interface VideoUploadProps {
  projectId: string;
  interviewId: string;
  onUploadComplete: (path: string, filename: string, size: number, type: string) => void;
  onUploadStateChange: (isUploading: boolean) => void;
}

export function VideoUpload({ 
  projectId, 
  interviewId, 
  onUploadComplete,
  onUploadStateChange 
}: VideoUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setError('Please select a valid video file');
      return;
    }

    // Validate file size (1GB limit)
    if (file.size > 1024 * 1024 * 1024) {
      setError('File size must be less than 1GB');
      return;
    }

    setUploading(true);
    onUploadStateChange(true);
    setError(null);

    try {
      // Sanitize the file name
      const sanitizedFileName = sanitizeFileName(file.name);
      const filePath = `${projectId}/${interviewId}/${sanitizedFileName}`;

      // Create a new File object with the sanitized name
      const sanitizedFile = new File([file], sanitizedFileName, {
        type: file.type,
        lastModified: file.lastModified,
      });

      const { error: uploadError } = await supabase.storage
        .from('interview_videos')
        .upload(filePath, sanitizedFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      onUploadComplete(filePath, sanitizedFileName, file.size, file.type);
    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.message);
    } finally {
      setUploading(false);
      onUploadStateChange(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-center">
          <span>{error}</span>
          <button 
            onClick={() => setError(null)}
            className="ml-auto"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex items-center justify-center w-full">
        <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
          <div className="flex flex-col items-center justify-center pt-5 pb-6 w-full px-4">
            {uploading ? (
              <div className="text-center w-full">
                <Loader2 className="h-8 w-8 animate-spin text-brand-orange mx-auto mb-4" />
                <p className="text-sm text-gray-500">
                  Uploading... this can take several minutes depending on file size
                </p>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 mb-4 text-gray-500" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  MP4, WebM or MOV (MAX. 1GB)
                </p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="video/*"
            onChange={handleFileSelect}
            disabled={uploading}
          />
        </label>
      </div>
    </div>
  );
}